import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { onIncomingCall, onCallResponse } from 'lib/socketHooks'
import api from '../a-portal/api'

const NO_RESPONSE_TIMEOUT = 60000

export function useCallManager({ myPublicProfileDid, myDisplayName }) {
  // Active outgoing call state
  const [outgoingCall, setOutgoingCall] = useState(null)

  // Incoming call notification state
  const [incomingCall, setIncomingCall] = useState(null)

  // Call alerts (declined/no-response messages)
  const [callAlerts, setCallAlerts] = useState([])

  // Active call (we've joined the room)
  const [activeCall, setActiveCall] = useState(null)

  const noResponseTimerRef = useRef(null)
  const outgoingCallRef = useRef(null)
  const activeCallRef = useRef(null)

  // Keep refs in sync with state
  useEffect(() => {
    outgoingCallRef.current = outgoingCall
  }, [outgoingCall])

  useEffect(() => {
    activeCallRef.current = activeCall
  }, [activeCall])

  useEffect(() => {
    return () => {
      if (noResponseTimerRef.current) {
        clearTimeout(noResponseTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!myPublicProfileDid) return

    const unsubIncoming = onIncomingCall((data) => {
      console.log('[CallManager] incomingCall event received:', {
        callerIdentity: data.callerIdentity,
        myPublicProfileDid,
        roomName: data.roomName,
      })

      if (data.callerIdentity === myPublicProfileDid) {
        console.log('[CallManager] Ignoring own call')
        return
      }
      if (activeCallRef.current) {
        console.log('[CallManager] Ignoring - already in a call')
        return
      }

      console.log('[CallManager] Showing incoming call popup')
      setIncomingCall(data)

      setTimeout(() => {
        setIncomingCall((prev) => {
          if (prev && prev.roomName === data.roomName) return null
          return prev
        })
      }, NO_RESPONSE_TIMEOUT)
    })

    return unsubIncoming
  }, [myPublicProfileDid])

  useEffect(() => {
    if (!myPublicProfileDid) return

    const unsubResponse = onCallResponse((data) => {
      const current = outgoingCallRef.current
      if (!current || current.roomName !== data.roomName) return
      if (data.responderIdentity === myPublicProfileDid) return

      if (data.response === 'declined') {
        setCallAlerts((prev) => [
          ...prev,
          {
            id: `${data.responderIdentity}-${Date.now()}`,
            type: 'declined',
            name: data.responderName,
            timestamp: Date.now(),
          },
        ])

        if (!current.isGroupCall) {
          setCallAlerts((prev) => [
            ...prev,
            {
              id: `notAnswering-${Date.now()}`,
              type: 'notAnswering',
              name: data.responderName,
              timestamp: Date.now(),
            },
          ])
          endOutgoingCall()
        }
      } else if (data.response === 'accepted') {
        setOutgoingCall((prev) => {
          if (!prev) return prev
          const responses = {
            ...prev.responses,
            [data.responderIdentity]: 'accepted',
          }
          return { ...prev, responses }
        })
      }
    })

    return unsubResponse
  }, [myPublicProfileDid])

  const initiateCall = useCallback(
    async ({
      chatChannelUid,
      callType,
      chatName,
      organizationApikey,
      memberUserDids,
    }) => {
      try {
        const result = await api.initiateCall({
          chatChannelUid,
          callType,
          callerName: myDisplayName,
          chatName,
          organizationApikey,
          memberUserDids,
        })

        const isGroupCall = memberUserDids && memberUserDids.length > 2

        const callData = {
          ...result,
          chatChannelUid,
          isGroupCall,
          responses: {},
          chatName: chatName || '',
        }

        setOutgoingCall(callData)
        setActiveCall({
          roomName: result.roomName,
          token: result.token,
          wsUrl: result.wsUrl,
          identity: result.identity,
          callType,
          chatChannelUid,
        })

        if (!isGroupCall) {
          noResponseTimerRef.current = setTimeout(() => {
            const current = outgoingCallRef.current
            if (!current) return
            const hasAccepted = Object.values(current.responses).includes(
              'accepted',
            )
            if (!hasAccepted) {
              setCallAlerts((prev) => [
                ...prev,
                {
                  id: `noResponse-${Date.now()}`,
                  type: 'notAnswering',
                  name: chatName || 'This person',
                  timestamp: Date.now(),
                },
              ])
              endOutgoingCall()
            }
          }, NO_RESPONSE_TIMEOUT)
        }

        return callData
      } catch (err) {
        console.error('Failed to initiate call:', err)
        throw err
      }
    },
    [myDisplayName, myPublicProfileDid],
  )

  const acceptCall = useCallback(async () => {
    if (!incomingCall) return null

    try {
      await api.respondToCall({
        chatChannelUid: incomingCall.chatChannelUid,
        roomName: incomingCall.roomName,
        response: 'accepted',
        responderName: myDisplayName,
      })

      const tokenData = await api.joinCall({
        roomName: incomingCall.roomName,
        participantName: myDisplayName,
      })

      const callData = {
        roomName: incomingCall.roomName,
        token: tokenData.token,
        wsUrl: tokenData.wsUrl,
        identity: tokenData.identity,
        callType: incomingCall.callType,
        chatChannelUid: incomingCall.chatChannelUid,
      }

      setActiveCall(callData)
      setIncomingCall(null)

      return callData
    } catch (err) {
      console.error('Failed to accept call:', err)
      setIncomingCall(null)
      throw err
    }
  }, [incomingCall, myDisplayName])

  const declineCall = useCallback(async () => {
    if (!incomingCall) return

    try {
      await api.respondToCall({
        chatChannelUid: incomingCall.chatChannelUid,
        roomName: incomingCall.roomName,
        response: 'declined',
        responderName: myDisplayName,
      })
    } catch (err) {
      console.error('Failed to send decline response:', err)
    }

    setIncomingCall(null)
  }, [incomingCall, myDisplayName])

  const endOutgoingCall = useCallback(() => {
    if (noResponseTimerRef.current) {
      clearTimeout(noResponseTimerRef.current)
      noResponseTimerRef.current = null
    }
    setOutgoingCall(null)
    setActiveCall(null)
  }, [])

  const leaveCall = useCallback(() => {
    if (noResponseTimerRef.current) {
      clearTimeout(noResponseTimerRef.current)
      noResponseTimerRef.current = null
    }
    setActiveCall(null)
    setOutgoingCall(null)

    if (window.location.pathname.startsWith('/video-call/')) {
      window.history.back()
    }
  }, [])

  const dismissAlert = useCallback((alertId) => {
    setCallAlerts((prev) => prev.filter((a) => a.id !== alertId))
  }, [])

  const dismissAllAlerts = useCallback(() => {
    setCallAlerts([])
  }, [])

  // Join a room by URL (direct link access). Shows lobby first.
  const joinRoomByUrl = useCallback((roomName) => {
    if (activeCallRef.current) return // already in a call
    setActiveCall({
      roomName,
      callType: 'video',
      showLobby: true,
    })
  }, [])

  const checkActiveCall = useCallback(async (chatChannelUid) => {
    try {
      const result = await api.checkActiveCall(chatChannelUid)
      return result
    } catch (err) {
      console.error('Failed to check active call:', err)
      return { hasActiveCall: false, roomName: null, participantCount: 0, isKicked: false }
    }
  }, [])

  const joinActiveCall = useCallback(({ roomName, callType, chatChannelUid, chatName, callerName }) => {
    if (activeCallRef.current) return // already in a call
    setActiveCall({
      roomName,
      callType: callType || 'video',
      chatChannelUid,
      chatName,
      callerName,
      showLobby: true,
    })
  }, [])

  const reportKick = useCallback(async (roomName, targetIdentity) => {
    try {
      await api.kickFromCall({ roomName, targetIdentity })
    } catch (err) {
      console.error('Failed to report kick:', err)
    }
  }, [])

  return {
    outgoingCall,
    incomingCall,
    activeCall,
    callAlerts,

    initiateCall,
    acceptCall,
    declineCall,
    endOutgoingCall,
    leaveCall,
    joinRoomByUrl,
    checkActiveCall,
    joinActiveCall,
    reportKick,
    dismissAlert,
    dismissAllAlerts,
  }
}
